import React from 'react';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip';

interface ExpandableInfoTagProps {
  id: string;
  expanded: boolean;
  onClick: () => void;
  dataTestId?: string;
  label?: string;
  text?: string;
  textComponent?: React.ReactNode;
  icon?: React.ReactElement;
  iconText?: string;
  count?: number;
  /** Rendered next to count as "count | secondaryCount" in the collapsed state. */
  secondaryCount?: number;
  /** Appended on its own line at the end of the tooltip and of the expanded content. */
  extraText?: string;
}

const ExpandableInfoTag: React.FC<ExpandableInfoTagProps> = ({
                                                               id,
                                                               expanded,
                                                               onClick,
                                                               dataTestId,
                                                               label,
                                                               text,
                                                               textComponent,
                                                               icon,
                                                               iconText,
                                                               count,
                                                               secondaryCount,
                                                               extraText,
                                                             }) => {

  const tooltipText = label
    ? `${label} ${text || iconText || ''}`
    : (text || iconText || '');

  const tooltipContent = extraText ? (
    <>
      <div>{tooltipText}</div>
      <div>{extraText}</div>
    </>
  ) : (
    tooltipText
  );


  const iconWithClass = icon
    ? React.cloneElement(icon, {
      className: `${icon.props.className || ''} flex-shrink-0 w-3 h-3`,
    })
    : null;

  return (
    <div
      id={id}
      data-testid={dataTestId}
      /* max-w-full + min-w-0: the tag is a flex item that would otherwise grow to its content width,
         so a note written as one long unbroken word would push the whole row sideways. */
      className="flex items-center min-h-[28px] max-w-full min-w-0 px-2 py-1 border border-solid border-content-brandMedium rounded text-xs tracking-[-0.12px] cursor-pointer transition-all duration-200 hover:bg-content-brandMedium"
      onClick={onClick}
    >
      {!expanded && (
        <div className="flex flex-row items-center gap-2">
          <CustomTooltip targetSelector={`#${id}`} content={tooltipContent} />
          {iconWithClass}
          {iconText && (
            <span className="text-xs break-words">{iconText}</span>
          )}
          {count && (
            <span className="text-sm">
              {count}
              {secondaryCount !== undefined && secondaryCount !== null && <span>{`\u00A0|\u00A0${secondaryCount}`}</span>}
            </span>
          )}
        </div>
      )}

      {expanded && (label || text || iconText || extraText) && (
        <div className="flex flex-col min-w-0">
          <div className="flex flex-row items-center text-xs gap-2">
            {iconWithClass}
            {iconText && (
              <span>{iconText}</span>
            )}
            {label && (
              <span>{label}</span>
            )}
          </div>
        {/* overflow-wrap:anywhere rather than break-words: it also shrinks the intrinsic minimum width,
            so a note with no spaces in it wraps instead of widening the tag and its table cell. */}
        <span className="[overflow-wrap:anywhere]">
          {(textComponent || text) && (
            <span className="font-medium">
              {textComponent ? textComponent : text}
            </span>
          )}
        </span>
          {extraText && (
            <span className="[overflow-wrap:anywhere] font-medium">{extraText}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpandableInfoTag;